import { useTranslation } from "react-i18next";

import type { OrderDeliveryView } from "@/api/types";

interface Props {
  selection: OrderDeliveryView;
  /** Map a process_type slug to a customer-facing label (laser → "Laser",
   *  print_3d → "3D tisk", …). Falls back to the slug when omitted. */
  formatProcessType?: (slug: string) => string;
  /** Hlavní řádek rychlosti; vypnout, když ho stránka ukazuje sama. */
  showHeader?: boolean;
}

/**
 * Renders the delivery speed the customer picked at checkout — the
 * customer shape of ``Order.delivery_selection`` (KAN-1957): the server
 * already resolved every speed to the name the tenant authored, so the
 * frozen snapshot's keys (``ladder_id``, ``ladder_code``, tier ids, line
 * ids) never reach this page.
 *
 * KAN-926: a per-operation row appears only when that operation ended up
 * on a different speed than the header, so a uniform pick reads as one line.
 */
export function OrderDeliverySnapshot({
  selection,
  formatProcessType = (slug) => slug,
  showHeader = true,
}: Props): JSX.Element | null {
  const { t } = useTranslation("order");
  const headerLabel = selection.label ?? "";
  const perProcess = selection.per_process ?? [];
  const fallbacks = selection.fallbacks ?? [];
  const lineOverrides = selection.line_overrides ?? [];

  const differingProcesses = perProcess.filter(
    (row) => row.label && row.label !== headerLabel,
  );

  if (!headerLabel && perProcess.length === 0 && lineOverrides.length === 0) {
    return null;
  }
  // Hlavní řádek rychlosti už stránka ukazuje jinde (KAN-1796: „Termín dodání
  // Standardní“ a „Vybraná rychlost Standardní“ pod sebou) - pak se tu
  // kreslí jen to, co řádek nenese: odlišnosti po procesech, náhrady, výjimky.
  const hasDetails =
    differingProcesses.length > 0 ||
    fallbacks.length > 0 ||
    lineOverrides.length > 0;
  if (!showHeader && !hasDetails) return null;

  return (
    <div
      className="mt-2 rounded border border-border bg-bg p-3 text-xs"
      data-testid="order-delivery-snapshot"
    >
      {showHeader && (
        <div className="flex items-baseline justify-between">
          <span className="text-ink-3">
            {t("deliveryPickerLabel", {
              defaultValue: "Vybraná rychlost",
            })}
          </span>
          <span className="font-medium text-ink">{headerLabel}</span>
        </div>
      )}

      {differingProcesses.length > 0 && (
        <ul
          className="mt-2 space-y-1 text-ink-2"
          data-testid="order-delivery-per-capacity"
        >
          {differingProcesses.map((row) => (
            <li
              key={row.process_type}
              className="flex items-baseline justify-between"
            >
              <span>{formatProcessType(row.process_type)}</span>
              <span className="text-ink">{row.label}</span>
            </li>
          ))}
        </ul>
      )}

      {fallbacks.length > 0 && (
        <ul
          className="mt-2 space-y-1 rounded bg-warn-bg px-2 py-1 text-warn"
          data-testid="order-delivery-fallbacks"
        >
          {fallbacks.map((f) => (
            <li key={f.process_type}>
              {t("deliveryFallback", {
                defaultValue:
                  "{{process}} → {{actual}} (požadováno: {{requested}})",
                process: formatProcessType(f.process_type),
                actual: f.actual_label,
                requested: f.requested_label,
              })}
            </li>
          ))}
        </ul>
      )}

      {lineOverrides.length > 0 && (
        <ul
          className="mt-2 space-y-1 text-ink-2"
          data-testid="order-delivery-line-overrides"
        >
          {lineOverrides.map((ov, idx) => (
            <li key={idx} className="flex items-baseline justify-between">
              <span>{ov.file_name}</span>
              <span className="text-ink">
                {ov.label}
                {ov.actual_label ? (
                  <span className="ml-1 text-warn">→ {ov.actual_label}</span>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
